import { Space, Row, Col } from 'antd';
import Button from 'components/new-ui/base/button/Button';
import DatePicker from 'components/new-ui/base/date-picker/DatePicker';
import SearchBar from 'components/new-ui/base/searchbar/SearchBar';
import Notification from 'components/ui/base/notification/Notification';
import useIndividuals from 'hooks/useIndividuals';
import useTeams from 'hooks/useTeams';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { archiveManifest, unarchiveManifest } from 'store/manifests/client';
import { Manifest } from 'store/manifests/types';
import { getErrorMessage } from 'utils/getErrorMessage';
import ArchiveManifestModal from 'views/manifests/components/ArchiveManifestModal';
import AssignManifestModal from 'views/manifests/components/assign-manifest/AssignManifestModal';
import ManifestFilterTags from 'views/manifests/components/ManifestFilterTags';
import ManifestsTable from 'views/manifests/components/manifests-table/ManifestsTable';
import UnarchiveManifestModal from 'views/manifests/components/UnarchiveManifestModal';
import ManifestsFiltersModal from 'views/manifests/dashboard/components/ManifestsFiltersModal';
import useManifestsTable from 'views/manifests/hooks/useManifestsTable';

const ManifestsTab = () => {
    const { teamId: teamIdParam } = useParams();
    const { listIndividuals } = useIndividuals();
    const { archivationStatus, setArchivationStatus, getTeamManifestsCount } = useTeams(teamIdParam);
    const navigate = useNavigate();

    const {
        tags,
        queryInputVal,
        appliedParams,
        isLoadingManifests,
        data,
        setQueryInputVal,
        setPage,
        clearAll,
        clearFilter,
        setArchived,
        date,
        pagination,
        filters,
        refetchManifests,
        handleTableChange,
        handleDateChange,
        handleFiltersChange,
    } = useManifestsTable({
        teamId: teamIdParam,
        archived: archivationStatus === 'archived' ? true : false,
    });

    const [selectedManifest, setSelectedManifest] = useState<Manifest | null>(null);
    const [archiving, setArchiving] = useState(false);
    const [unarchiving, setUnarchiving] = useState(false);
    const [showFilterModal, setShowFilterModal] = useState(false);
    const [showAssignManifestModal, setShowAssignManifestModal] = useState(false);
    const [showArchiveManifestModal, setShowArchiveManifestModal] = useState(false);
    const [showUnarchiveManifestModal, setShowUnarchiveManifestModal] = useState(false);
    const { sort, sortDirection } = appliedParams;

    const handleManifestClick = (record: Manifest) => {
        const manifestId = record.id;
        navigate(`/manifests/${manifestId}`);
    };

    const handleOpenArchiveModal = (m: Manifest) => {
        setSelectedManifest(m);
        setShowArchiveManifestModal(true);
    };

    const handleOpenUnarchiveModal = (m: Manifest) => {
        setSelectedManifest(m);
        setShowUnarchiveManifestModal(true);
    };

    const handleConfirmArchive = async () => {
        if (!selectedManifest) return;
        try {
            setArchiving(true);
            await archiveManifest(selectedManifest.id);
            Notification({
                type: 'success',
                placement: 'top',
                messageTitle: 'Manifest Archived',
                description: 'The manifest has been successfully added to the archive',
            });
            refetchManifests();
            if (teamIdParam) getTeamManifestsCount(teamIdParam);
            setShowArchiveManifestModal(false);
            setSelectedManifest(null);
        } catch (err) {
            const errorMessage = getErrorMessage(err);
            Notification({
                type: 'error',
                placement: 'top',
                messageTitle: 'Manifest Archivation Error',
                description: errorMessage || 'Could not arhive manifest. Please try again.',
            });
        } finally {
            setArchiving(false);
        }
    };

    const handleConfirmUnarchive = async () => {
        if (!selectedManifest) return;
        try {
            setUnarchiving(true);
            await unarchiveManifest(selectedManifest.id);
            Notification({
                type: 'success',
                placement: 'top',
                messageTitle: 'Manifest Unarchived',
                description: 'The manifest will now be activated.',
            });
            refetchManifests();
            if (teamIdParam) getTeamManifestsCount(teamIdParam);
            setShowUnarchiveManifestModal(false);
            setSelectedManifest(null);
        } catch (err) {
            const errorMessage = getErrorMessage(err);
            Notification({
                type: 'error',
                placement: 'top',
                messageTitle: 'Manifest Unarchivation Error',
                description: errorMessage || 'Could not activate the manifest. Please try again.',
            });
        } finally {
            setUnarchiving(false);
        }
    };

    // Sync the Archived switch filter with the URL params
    useEffect(() => {
        setArchived(archivationStatus === 'archived' ? true : false);
    }, [appliedParams.archived, archivationStatus, setArchivationStatus, setArchived]);

    useEffect(() => {
        listIndividuals({ size: 200 });
    }, [listIndividuals]);

    return (
        <>
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Row justify="space-between">
                    <Col>
                        <Space direction="vertical">
                            <Space size={8}>
                                <SearchBar
                                    inputPlaceholder="Search manifests"
                                    value={queryInputVal}
                                    onSearch={(val) => {
                                        setQueryInputVal(val);
                                        setPage(0);
                                    }}
                                    onClear={() => {
                                        setQueryInputVal('');
                                        setPage(0);
                                    }}
                                />
                                <Button onClick={() => setShowFilterModal(true)}>Filter</Button>
                            </Space>
                        </Space>
                    </Col>
                    <Col>
                        <DatePicker value={date} onChange={handleDateChange} />
                    </Col>
                </Row>

                <Row>
                    <ManifestFilterTags
                        tags={tags}
                        ownerId={appliedParams.ownerId}
                        teamId={appliedParams.teamId}
                        createdBy={appliedParams.createdBy}
                        onClearTag={clearFilter}
                        onClearAll={clearAll}
                    />
                </Row>

                <ManifestsTable
                    manifests={data?.list}
                    loading={isLoadingManifests}
                    onArchiveManifest={handleOpenArchiveModal}
                    onUnarchiveManifest={handleOpenUnarchiveModal}
                    onAssignManifest={(manifest) => {
                        setSelectedManifest(manifest);
                        setShowAssignManifestModal(true);
                    }}
                    onRowClick={handleManifestClick}
                    onChange={handleTableChange}
                    paginator={pagination}
                    currentSortDirection={sortDirection}
                    currentSortParam={sort}
                />
            </Space>

            <ManifestsFiltersModal
                visible={showFilterModal}
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onClose={() => setShowFilterModal(false)}
                onClearAll={() => clearAll()}
                onClearFilter={clearFilter}
                optionalFilters={{
                    assignedTo: true,
                }}
            />

            {selectedManifest && (
                <AssignManifestModal
                    manifest={selectedManifest}
                    open={showAssignManifestModal}
                    onClose={() => setShowAssignManifestModal(false)}
                    onSuccess={() => refetchManifests()}
                />
            )}

            <ArchiveManifestModal
                isArchiving={archiving}
                open={showArchiveManifestModal}
                onConfirm={handleConfirmArchive}
                onClose={() => setShowArchiveManifestModal(false)}
            />
            <UnarchiveManifestModal
                isUnarchiving={unarchiving}
                open={showUnarchiveManifestModal}
                onConfirm={handleConfirmUnarchive}
                onClose={() => setShowUnarchiveManifestModal(false)}
            />
        </>
    );
};

export default ManifestsTab;
